import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  RACE_DAYS_COLLECTION,
  AUDIT_LOG_SUBCOLLECTION,
  emptyRaceDay,
} from './raceDaySchema';

// ── Reads ───────────────────────────────────────────────────────────

/** Fetch all race days for a club, ordered by date descending. */
export async function listRaceDays(clubId) {
  const q = query(
    collection(db, RACE_DAYS_COLLECTION),
    where('clubId', '==', clubId),
    orderBy('date', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Fetch all race days for a specific event. */
export async function listEventRaceDays(clubId, eventId) {
  const q = query(
    collection(db, RACE_DAYS_COLLECTION),
    where('clubId', '==', clubId),
    where('event_id', '==', eventId),
    orderBy('date', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Fetch a single race day by document ID. */
export async function getRaceDay(docId) {
  const snap = await getDoc(doc(db, RACE_DAYS_COLLECTION, docId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/** Fetch the audit log for a race day, ordered by timestamp. */
export async function getAuditLog(raceDayId) {
  const q = query(
    collection(db, RACE_DAYS_COLLECTION, raceDayId, AUDIT_LOG_SUBCOLLECTION),
    orderBy('timestamp', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Real-time subscriptions ─────────────────────────────────────────

/**
 * Subscribe to a single race day document. Returns an unsubscribe function.
 * @param {string} raceDayId
 * @param {(raceDay: Object|null) => void} onData
 * @param {(error: Error) => void} [onError]
 */
export function subscribeToRaceDay(raceDayId, onData, onError) {
  return onSnapshot(
    doc(db, RACE_DAYS_COLLECTION, raceDayId),
    (snap) => {
      if (!snap.exists()) { onData(null); return; }
      onData({ id: snap.id, ...snap.data() });
    },
    onError,
  );
}

/**
 * Subscribe to all race days for a club, ordered by date descending.
 * Returns an unsubscribe function.
 * @param {string} clubId
 * @param {(raceDays: Object[]) => void} onData
 * @param {(error: Error) => void} [onError]
 */
export function subscribeToClubRaceDays(clubId, onData, onError) {
  const q = query(
    collection(db, RACE_DAYS_COLLECTION),
    where('clubId', '==', clubId),
    orderBy('date', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, onError);
}

// ── Writes (all audit-logged) ───────────────────────────────────────

/**
 * Write an audit log entry to the subcollection.
 * @param {string} raceDayId
 * @param {Object} entry - { author, action, field, entry_id?, before?, after }
 */
async function writeAuditEntry(raceDayId, entry) {
  await addDoc(
    collection(db, RACE_DAYS_COLLECTION, raceDayId, AUDIT_LOG_SUBCOLLECTION),
    {
      ...entry,
      timestamp: serverTimestamp(),
    },
  );
}

/** Create a new race day document. Returns the new document ID. */
export async function createRaceDay({ clubId, eventId, date, uid }) {
  const data = emptyRaceDay({ clubId, eventId, date, createdBy: uid });
  const ref = await addDoc(collection(db, RACE_DAYS_COLLECTION), {
    ...data,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  await writeAuditEntry(ref.id, {
    author: uid,
    action: 'create_race_day',
    field: 'race_day',
    after: { clubId, event_id: eventId, date, status: data.status },
  });

  return ref.id;
}

/** Update race day status. */
export async function updateRaceDayStatus(raceDayId, newStatus, uid) {
  const ref = doc(db, RACE_DAYS_COLLECTION, raceDayId);
  const snap = await getDoc(ref);
  const oldStatus = snap.data()?.status;

  await updateDoc(ref, {
    status: newStatus,
    updated_at: serverTimestamp(),
    updated_by: uid,
  });

  await writeAuditEntry(raceDayId, {
    author: uid,
    action: 'update_status',
    field: 'status',
    before: { status: oldStatus },
    after: { status: newStatus },
  });
}

/**
 * Append an entry to an array field. Handles the supersedes pattern:
 * if `entry.supersedes` is set, the old entry stays untouched and the
 * new entry is appended alongside it.
 *
 * @param {string} raceDayId
 * @param {string} field - Array field name (e.g. 'conditions', 'officers')
 * @param {string} action - Audit action name
 * @param {Object} entry - The entry to append (must include `id`)
 * @param {string} uid - Author UID
 * @param {Object} [supersededEntry] - The original entry being corrected (for audit before)
 */
async function appendEntry(raceDayId, field, action, entry, uid, supersededEntry) {
  const ref = doc(db, RACE_DAYS_COLLECTION, raceDayId);

  await updateDoc(ref, {
    [field]: arrayUnion(entry),
    updated_at: serverTimestamp(),
    updated_by: uid,
  });

  await writeAuditEntry(raceDayId, {
    author: uid,
    action: entry.supersedes ? 'correct_entry' : action,
    field,
    entry_id: entry.id,
    ...(supersededEntry ? { before: supersededEntry } : {}),
    after: entry,
  });
}

/** Add a weather/sea condition observation. */
export async function addCondition(raceDayId, condition, uid, supersededEntry) {
  await appendEntry(raceDayId, 'conditions', 'add_condition', condition, uid, supersededEntry);
}

/** Add an officer assignment. */
export async function addOfficer(raceDayId, officer, uid, supersededEntry) {
  await appendEntry(raceDayId, 'officers', 'add_officer', officer, uid, supersededEntry);
}

/** Add a committee decision. */
export async function addDecision(raceDayId, decision, uid, supersededEntry) {
  await appendEntry(raceDayId, 'decisions', 'add_decision', decision, uid, supersededEntry);
}

/** Add a fleet start record. */
export async function addFleetStart(raceDayId, fleetStart, uid, supersededEntry) {
  await appendEntry(raceDayId, 'fleet_starts', 'add_fleet_start', fleetStart, uid, supersededEntry);
}

/** Add a finish record. */
export async function addFinishRecord(raceDayId, finishRecord, uid, supersededEntry) {
  await appendEntry(raceDayId, 'finish_records', 'add_finish_record', finishRecord, uid, supersededEntry);
}

/** Add a broadcast record. */
export async function addBroadcast(raceDayId, broadcast, uid, supersededEntry) {
  await appendEntry(raceDayId, 'broadcasts', 'add_broadcast', broadcast, uid, supersededEntry);
}

/** Set or update the course setup. Audit-logs the before/after. */
export async function setCourseSetup(raceDayId, courseSetup, uid) {
  const ref = doc(db, RACE_DAYS_COLLECTION, raceDayId);
  const snap = await getDoc(ref);
  const oldSetup = snap.data()?.course_setup ?? null;

  await updateDoc(ref, {
    course_setup: courseSetup,
    updated_at: serverTimestamp(),
    updated_by: uid,
  });

  await writeAuditEntry(raceDayId, {
    author: uid,
    action: 'set_course',
    field: 'course_setup',
    ...(oldSetup ? { before: oldSetup } : {}),
    after: courseSetup,
  });
}
